import React, { useState, useEffect, useRef } from "react";
import {
  Layout,
  Card,
  Space,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Table,
  Statistic,
  Typography,
  message,
  InputNumber,
  Tooltip,
  Dropdown,
} from "antd";
import {
  FilterOutlined,
  DollarOutlined,
  CarOutlined,
  FileTextOutlined,
  PercentageOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getVehicleStatistics } from "../../../api/statistics/statisticsRequest";
import { getZones } from "../../../api/zone/zoneRequest";
import { getTypeInfraction } from "../../../api/typeInfraction/infractionType";
import { formatAmountInFrench } from "../../../utils/constants";
import { useSelector } from "react-redux";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title } = Typography;

const VehicleStatistics = () => {
  const [dateRange, setDateRange] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedTypeInfraction, setSelectedTypeInfraction] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [maxResults, setMaxResults] = useState(100);
  const [minViolations, setMinViolations] = useState(1);

  const [zones, setZones] = useState([]);
  const [typeInfractions, setTypeInfractions] = useState([]);
  const [vehicleData, setVehicleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);

  const user = useSelector((state) => state.user.user);
  const workerPdfRef = useRef(null);
  const workerExcelRef = useRef(null);

  useEffect(() => {
    fetchZones();
    fetchTypeInfractions();

    // Initialize workers
    workerPdfRef.current = new Worker(
      new URL("../../../workers/pdfWorker.js", import.meta.url)
    );
    workerExcelRef.current = new Worker(
      new URL("../../../workers/excelWorker.js", import.meta.url)
    );

    workerPdfRef.current.onmessage = (e) => {
      const {
        status,
        blobURL,
        operation,
        filename,
        message: workerMessage,
      } = e.data;

      switch (status) {
        case "starting":
          console.log("PDF generation starting...");
          setPdfExporting(true);
          break;
        case "generating":
          console.log("Creating PDF document...");
          break;
        case "complete":
          console.log("PDF generation complete");

          if (operation === "download") {
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = filename || "file.pdf";
            link.click();
            URL.revokeObjectURL(blobURL);
          } else if (operation === "open") {
            window.open(blobURL, "_blank");
          }

          setPdfExporting(false);
          break;
        case "error":
          console.error("PDF generation error:", workerMessage);
          message.error(
            `Erreur lors de la génération du PDF: ${workerMessage}`
          );
          setPdfExporting(false);
          break;
        default:
          console.warn("Unknown status:", status);
          break;
      }
    };

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error } = e.data;

      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
        message.success("Export Excel terminé");
      } else {
        message.error(
          `Erreur lors de la génération du fichier Excel: ${error}`
        );
      }
    };

    return () => {
      workerPdfRef.current?.terminate();
      workerExcelRef.current?.terminate();
    };
  }, []);

  const filterVehicleStatistics = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.error("Veuillez sélectionner une plage de dates");
      return;
    }

    setLoading(true);
    try {
      const data = await getVehicleStatistics({
        start_date: dateRange[0].format("YYYY-MM-DD"),
        end_date: dateRange[1].format("YYYY-MM-DD"),
        max_results: maxResults,
        type_infraction_id: selectedTypeInfraction,
        status: selectedStatus,
        zone_id: selectedZone,
        min_violations: minViolations,
      });
      setVehicleData(data);
      message.success("Données récupérées avec succès");
    } catch (error) {
      setVehicleData(null);
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async () => {
    try {
      const data = await getZones();
      setZones(data.data);
    } catch (error) {
      message.error("Échec de la récupération des zones");
    }
  };

  const fetchTypeInfractions = async () => {
    try {
      const data = await getTypeInfraction();
      setTypeInfractions(data);
    } catch (error) {
      message.error("Échec de la récupération des types d'infractions");
    }
  };

  const handleExcelClick = async () => {
    if (!vehicleData || !vehicleData.vehicles) {
      message.error("Aucune donnée à exporter");
      return;
    }

    const timestamp = new Date().toISOString().replace(/:/g, "-");
    const filename = `vehicules_infractions_${timestamp}.xlsx`;
    const baseUrl = window.location.origin;

    // Prepare data for Excel export
    const formattedData = vehicleData.vehicles.map((vehicle) => ({
      matricule: vehicle.matricule,
      brand: vehicle.brand || "N/A",
      model: vehicle.model || "N/A",
      color: vehicle.color || "N/A",
      country: vehicle.country,
      total_violations: vehicle.total_violations,
      pending_violations: vehicle.pending_violations,
      paid_violations: vehicle.paid_violations,
      canceled_violations: vehicle.canceled_violations,
      total_amount: vehicle.total_amount,
      paid_amount: vehicle.paid_amount,
      pending_amount: vehicle.pending_amount,
      violation_types: vehicle.violation_types,
      violation_zones: vehicle.violation_zones,
      first_violation_date: vehicle.first_violation_date,
      last_violation_date: vehicle.last_violation_date,
      violation_period_days: Math.round(vehicle.violation_period_days),
      violations_per_month: vehicle.violations_per_month,
      payment_rate_percentage: vehicle.payment_rate_percentage / 100,
      average_violation_amount: vehicle.average_violation_amount,
    }));

    const data = [
      [
        "Statistiques des Véhicules",
        `Période: ${dateRange[0]?.format(
          "YYYY-MM-DD"
        )} - ${dateRange[1]?.format("YYYY-MM-DD")}`,
        `Résultats: ${vehicleData.vehicles.length} véhicules`,
      ],
      [],
      [],
      user?.full_name || "Système",
      [],
      [
        [
          { text: "Matricule", type: "string", key: "matricule" },
          { text: "Marque", type: "string", key: "brand" },
          { text: "Modèle", type: "string", key: "model" },
          { text: "Couleur", type: "string", key: "color" },
          { text: "Pays", type: "string", key: "country" },
          { text: "Total Infractions", type: "int", key: "total_violations" },
          {
            text: "Infractions en Attente",
            type: "int",
            key: "pending_violations",
          },
          { text: "Infractions Payées", type: "int", key: "paid_violations" },
          {
            text: "Infractions Annulées",
            type: "int",
            key: "canceled_violations",
          },
          { text: "Montant Total", type: "int", key: "total_amount" },
          { text: "Montant Payé", type: "int", key: "paid_amount" },
          { text: "Montant en Attente", type: "int", key: "pending_amount" },
          {
            text: "Taux de Paiement (%)",
            type: "precent",
            key: "payment_rate_percentage",
          },
          {
            text: "Période (jours)",
            type: "int",
            key: "violation_period_days",
          },
          {
            text: "Infractions/Mois",
            type: "int",
            key: "violations_per_month",
          },
          {
            text: "Montant Moyen",
            type: "int",
            key: "average_violation_amount",
          },
          {
            text: "Types de Infractions",
            type: "string",
            key: "violation_types",
          },
          {
            text: "Zones de Infractions",
            type: "string",
            key: "violation_zones",
          },
          {
            text: "Première Infractions",
            type: "string",
            key: "first_violation_date",
          },
          {
            text: "Dernière Infractions",
            type: "string",
            key: "last_violation_date",
          },
        ],
      ],
      [formattedData],
      baseUrl,
    ];

    workerExcelRef.current.postMessage({ data, filename });
  };

  const handlePdfClick = (operation) => {
    if (!vehicleData || !vehicleData.vehicles) {
      message.error("Aucune donnée à exporter");
      return;
    }

    const timestamp = new Date().toISOString().replace(/:/g, "-");
    const filename = `vehicules_infractions_${timestamp}.pdf`;
    const baseUrl = window.location.origin;

    // Prepare data for PDF export
    const formattedData = vehicleData.vehicles.map((vehicle) => ({
      matricule: vehicle.matricule,
      brand: vehicle.brand || "N/A",
      total_violations: vehicle.total_violations,
      total_amount: vehicle.total_amount,
      payment_rate_percentage: `${vehicle.payment_rate_percentage}%`,
      violation_types:
        vehicle.violation_types?.length > 30
          ? `${vehicle.violation_types.substring(0, 30)}...`
          : vehicle.violation_types,
      violation_zones:
        vehicle.violation_zones?.length > 20
          ? `${vehicle.violation_zones.substring(0, 20)}...`
          : vehicle.violation_zones,
      first_violation_date: vehicle.first_violation_date?.split(" ")[0],
      last_violation_date: vehicle.last_violation_date?.split(" ")[0],
    }));

    const data = [
      [
        "Statistiques des Véhicules avec le Plus de Infractions",
        `Période: ${dateRange[0]?.format(
          "YYYY-MM-DD"
        )} - ${dateRange[1]?.format("YYYY-MM-DD")}`,
        `Date de génération: ${new Date().toLocaleDateString("fr-FR")}`,
        `Généré par: ${user?.full_name || "Système"}`,
      ],
      [],
      [],
      `Résumé: ${vehicleData.vehicles.length} véhicules | ${vehicleData.summary.total_amount_all_violations} MAD`,
      [],
      [
        [
          { text: "Matricule", type: "string", key: "matricule" },
          { text: "Marque", type: "string", key: "brand" },
          {
            text: "Infractions",
            type: "string",
            key: "total_violations",
            align: "center",
          },
          {
            text: "Montant (MAD)",
            type: "string",
            key: "total_amount",
            align: "right",
          },
          {
            text: "Taux Paiement",
            type: "string",
            key: "payment_rate_percentage",
            align: "center",
          },
          { text: "Types", type: "string", key: "violation_types" },
          { text: "Zones", type: "string", key: "violation_zones" },
          { text: "Première", type: "string", key: "first_violation_date" },
          { text: "Dernière", type: "string", key: "last_violation_date" },
        ],
      ],
      [formattedData],
      baseUrl,
    ];

    workerPdfRef.current.postMessage({ data, operation, filename });
  };

  const columns = [
    {
      title: "Matricule",
      dataIndex: "matricule",
      key: "matricule",
      fixed: "left",
      width: 120,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Véhicule",
      key: "vehicle_info",
      width: 100,
      render: (_, record) => (
        <div>
          <div>
            <strong>{record.brand || "N/A"}</strong>
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {record.model || "N/A"}
          </div>
          <div style={{ fontSize: "11px", color: "#999" }}>
            {record.country}
          </div>
        </div>
      ),
    },
    {
      title: "Total Infractions",
      dataIndex: "total_violations",
      key: "total_violations",
      width: 120,
      sorter: (a, b) => a.total_violations - b.total_violations,
      render: (value) => (
        <div style={{ textAlign: "center" }}>
          <div
            style={{ fontSize: "16px", fontWeight: "bold", color: "#ff4d4f" }}
          >
            {value}
          </div>
        </div>
      ),
    },
    {
      title: "Status Infractions",
      key: "violation_status",
      width: 150,
      render: (_, record) => (
        <div style={{ fontSize: "12px" }}>
          <div>🟡 En attente: {record.pending_violations}</div>
          <div>🟢 Payées: {record.paid_violations}</div>
          <div>🔴 Annulées: {record.canceled_violations}</div>
        </div>
      ),
    },
    {
      title: "Montants",
      key: "amounts",
      width: 150,
      render: (_, record) => (
        <div style={{ fontSize: "12px" }}>
          <div>
            <strong>Total: {formatAmountInFrench(record.total_amount)}</strong>
          </div>
          <div>🟢 Payé: {formatAmountInFrench(record.paid_amount)}</div>
          <div>
            🟡 En attente: {formatAmountInFrench(record.pending_amount)}
          </div>
        </div>
      ),
    },
    {
      title: "Taux de Paiement",
      dataIndex: "payment_rate_percentage",
      key: "payment_rate_percentage",
      width: 120,
      sorter: (a, b) => a.payment_rate_percentage - b.payment_rate_percentage,
      render: (value) => (
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              color:
                value > 50 ? "#52c41a" : value > 20 ? "#faad14" : "#ff4d4f",
            }}
          >
            {value}%
          </div>
        </div>
      ),
    },
    {
      title: "Période d'Activité",
      key: "activity_period",
      width: 150,
      render: (_, record) => (
        <div style={{ fontSize: "11px" }}>
          <div>
            <strong>{Math.round(record.violation_period_days)} jours</strong>
          </div>
          <div>{record.violations_per_month} infractions/mois</div>
          <div>{record.first_violation_date?.split(" ")[0]}</div>
          <div>→ {record.last_violation_date?.split(" ")[0]}</div>
        </div>
      ),
    },
    {
      title: "Types de Infractions",
      dataIndex: "violation_types",
      key: "violation_types",
      width: 200,
      render: (text) => (
        <Tooltip title={text}>
          <div style={{ fontSize: "11px" }}>
            {text?.length > 50 ? `${text.substring(0, 50)}...` : text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Zones",
      dataIndex: "violation_zones",
      key: "violation_zones",
      width: 150,
      render: (text) => (
        <Tooltip title={text}>
          <div style={{ fontSize: "11px" }}>
            {text?.length > 30 ? `${text.substring(0, 30)}...` : text}
          </div>
        </Tooltip>
      ),
    },
  ];

  const getViolationTypesChart = () => {
    if (!vehicleData?.top_violation_types) return null;

    return {
      chart: { 
        type: "column",
        zoomType: 'xy'
      },
      title: { text: "Types de Infractions les Plus Fréquents" },
      xAxis: {
        categories: vehicleData.top_violation_types.map((item) =>
          item.violation_type.length > 20
            ? `${item.violation_type.substring(0, 20)}...`
            : item.violation_type
        ),
        labels: { rotation: -45 },
      },
      credits: {
        enabled: false,
      },
      yAxis: [
        {
          title: { 
            text: "Nombre de Infractions",
            style: { color: "#ff4d4f" }
          },
          labels: { 
            format: "{value}",
            style: { color: "#ff4d4f" }
          },
        },
        {
          title: { 
            text: "Montant (MAD)",
            style: { color: "#1890ff" }
          },
          opposite: true,
          labels: { 
            format: "{value} MAD",
            style: { color: "#1890ff" }
          },
        },
      ],
      series: [
        {
          name: "Nombre de Infractions",
          type: "column",
          yAxis: 0,
          data: vehicleData.top_violation_types.map(
            (item) => item.violation_count
          ),
          color: "#ff4d4f",
          tooltip: {
            valueSuffix: ' infractions'
          }
        },
        {
          name: "Montant Total",
          type: "spline",
          yAxis: 1,
          data: vehicleData.top_violation_types.map(
            (item) => parseFloat(item.total_amount) || 0
          ),
          color: "#1890ff",
          marker: {
            enabled: true,
            radius: 4
          },
          tooltip: {
            valueSuffix: ' MAD'
          }
        },
      ],
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom'
      },
      tooltip: {
        shared: true,
        formatter: function () {
          // const index = this.x;
          const point = this.points[0];
          const index = point.point.index;
          const violationType = vehicleData.top_violation_types[index];
          let tooltip = `<b>${violationType.name}</b><br/>`;
          
          this.points.forEach(point => {
            if (point.series.name === "Nombre de Infractions") {
              tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y}</b><br/>`;
            } else {
              tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${formatAmountInFrench(point.y)}</b><br/>`;
            }
          });
          
          return tooltip;
        },
      },
    };
  };

  // Helper functions for export dropdown
  const getPdfMenuItems = (handlePdfClick, pdfExporting) => [
    {
      key: "print",
      label: <span className="hover:text-red-500">Télécharger</span>,
      onClick: () => handlePdfClick("download"),
      disabled: pdfExporting,
    },
    {
      key: "open",
      label: <span className="hover:text-red-500">Ouvrir</span>,
      onClick: () => handlePdfClick("open"),
      disabled: pdfExporting,
    },
  ];

  return (
    <Layout className="min-h-screen ">
      <Content style={{ padding: "24px" }} className="py-6 sm:px-6 lg:px-8">
        {/* Filters */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <label>Période *</label>
              <RangePicker
                style={{ width: "100%" }}
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
              />
            </Col>

            <Col span={6}>
              <label>Zone</label>
              <Select
                style={{ width: "100%" }}
                value={selectedZone}
                onChange={setSelectedZone}
                allowClear
                placeholder="Toutes les zones"
              >
                {zones.map((zone) => (
                  <Option key={zone.id} value={zone.id}>
                    {zone.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={5}>
              <label>Type d'Infraction</label>
              <Select
                style={{ width: "100%" }}
                value={selectedTypeInfraction}
                onChange={setSelectedTypeInfraction}
                allowClear
                placeholder="Tous"
              >
                {typeInfractions.map((type) => (
                  <Option key={type.id} value={type.id}>
                    {type.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={3}>
              <label>Statut</label>
              <Select
                style={{ width: "100%" }}
                value={selectedStatus}
                onChange={setSelectedStatus}
                allowClear
                placeholder="Tous"
              >
                <Option value="pending">En attente</Option>
                <Option value="paid">Payé</Option>
                <Option value="canceled">Annulé</Option>
              </Select>
            </Col>
          </Row>
          <Row style={{ marginTop: 16 }} gutter={16} className="flex items-end">
            <Col span={4}>
              <label>Nombre Max de Résultats</label>
              <InputNumber
                style={{ width: "100%" }}
                value={maxResults}
                onChange={setMaxResults}
                min={1}
                max={30000}
                placeholder="100"
              />
            </Col>
            <Col span={4}>
              <label>Min. Infractions</label>
              <InputNumber
                style={{ width: "100%" }}
                value={minViolations}
                onChange={setMinViolations}
                min={1}
                placeholder="1"
              />
            </Col>
            <Col span={4}>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={filterVehicleStatistics}
                loading={loading}
              >
                Filtrer
              </Button>
            </Col>
          </Row>
        </Card>

        {vehicleData && (
          <>
            {/* Summary Cards */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Véhicules avec Infractions"
                    value={vehicleData.summary.total_vehicles_with_violations}
                    prefix={<CarOutlined />}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Infractions"
                    value={vehicleData.summary.total_violations_in_period}
                    prefix={<WarningOutlined />}
                    valueStyle={{ color: "#ff4d4f" }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Montant Total"
                    value={`${formatAmountInFrench(
                      vehicleData.summary.total_amount_all_violations
                    )} `}
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Taux de Recouvrement"
                    value={vehicleData.summary.collection_rate_percentage}
                    suffix="%"
                    valueStyle={{ color: "#faad14" }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Chart */}
            {vehicleData.top_violation_types?.length > 0 && (
              <Card style={{ marginBottom: 24 }}>
                <HighchartsReact
                  highcharts={Highcharts}
                  options={getViolationTypesChart()}
                />
              </Card>
            )}

            {/* Table */}
            <Card>
              <div
                style={{
                  marginBottom: 16,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Title level={4} style={{ margin: 0 }}>
                  Liste des Véhicules ({vehicleData.vehicles?.length || 0}{" "}
                  résultats)
                </Title>
                <Space>
                  <Button
                    icon={<FileTextOutlined />}
                    onClick={handleExcelClick}
                    className="hover:border-green-700 hover:text-green-500"
                  >
                    Export Excel
                  </Button>
                  <Dropdown
                    menu={{
                      items: getPdfMenuItems(handlePdfClick, pdfExporting),
                    }}
                    disabled={pdfExporting}
                    className="hover:border-red-700 hover:text-red-500"
                  >
                    <Button icon={<FileTextOutlined />} disabled={pdfExporting}>
                      Export PDF{" "}
                      {pdfExporting ? (
                        "..."
                      ) : (
                        <span style={{ marginLeft: 5 }}>▼</span>
                      )}
                    </Button>
                  </Dropdown>
                </Space>
              </div>
              <Table
                columns={columns}
                dataSource={vehicleData.vehicles}
                rowKey="vehicle_id"
                scroll={{ x: 1400 }}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],

                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} sur ${total} véhicules`,
                }}
                size="small"
              />
            </Card>
          </>
        )}
      </Content>
    </Layout>
  );
};

export default VehicleStatistics;
